import axios from "axios";

const TELEGRAMUP_URL =
  process.env.REACT_APP_TELEGRAMUP_URL || "http://localhost:8005";

const API_BASE = `${TELEGRAMUP_URL}/api/v1`;

const TOKEN_KEY = "lala_webapp_token";
const TG_KEY = "lala_webapp_tg";

function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function currentTgId() {
  return window.Telegram?.WebApp?.initDataUnsafe?.user?.id || null;
}

function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
}

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Exchange Telegram WebApp initData for a JWT.
 * Must be called from inside a Telegram WebApp (window.Telegram.WebApp.initData).
 */
async function loginWithWebApp() {
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData) {
    throw new Error("Not running inside Telegram WebApp");
  }
  const res = await axios.post(`${API_BASE}/auth/webapp-login`, {
    init_data: initData,
  });
  setToken(res.data.access_token);
  const tg = currentTgId();
  if (tg) localStorage.setItem(TG_KEY, String(tg));
  return res.data.access_token;
}

/** Ensure we have a valid token for the CURRENT telegram user, logging in if necessary. */
async function ensureAuth() {
  const tg = currentTgId();
  if (tg && localStorage.getItem(TG_KEY) !== String(tg)) {
    localStorage.removeItem(TOKEN_KEY);
  }
  let token = getToken();
  if (!token) {
    token = await loginWithWebApp();
  }
  return token;
}

/**
 * Run an authenticated request; on 401/403 clear the stale token,
 * re-login once, and retry.
 */
async function callWithAuth(fn) {
  await ensureAuth();
  try {
    return await fn();
  } catch (e) {
    if (e.response?.status === 401 || e.response?.status === 403) {
      localStorage.removeItem(TOKEN_KEY);
      await loginWithWebApp();
      return await fn();
    }
    throw e;
  }
}

async function initiateDeposit(amount, method) {
  const res = await callWithAuth(() =>
    axios.post(`${API_BASE}/deposits/initiate`, { amount, method }, { headers: authHeaders() })
  );
  return res.data;
}

async function confirmDeposit(text) {
  const res = await callWithAuth(() =>
    axios.post(`${API_BASE}/deposits/confirm`, { text }, { headers: authHeaders() })
  );
  return res.data;
}

async function initiateWithdrawal(amount, method, recipientPhone) {
  const res = await callWithAuth(() =>
    axios.post(`${API_BASE}/withdrawals/initiate`, { amount, method, recipient_phone: recipientPhone }, { headers: authHeaders() })
  );
  return res.data;
}

async function getBonusStatus() {
  const res = await callWithAuth(() =>
    axios.get(`${API_BASE}/bonus/status`, { headers: authHeaders() })
  );
  return res.data;
}

async function claimBonus() {
  const res = await callWithAuth(() =>
    axios.post(`${API_BASE}/bonus/claim`, {}, { headers: authHeaders() })
  );
  return res.data;
}

async function getBestPlayerProgress() {
  const res = await callWithAuth(() =>
    axios.get(`${API_BASE}/withdrawals/best-player`, { headers: authHeaders() })
  );
  return res.data;
}

async function initiatePromoWithdrawal(amount, method, recipientPhone) {
  const res = await callWithAuth(() =>
    axios.post(`${API_BASE}/withdrawals/initiate-promo`, { amount, method, recipient_phone: recipientPhone }, { headers: authHeaders() })
  );
  return res.data;
}

async function getWithdrawalAvailable() {
  const res = await callWithAuth(() =>
    axios.get(`${API_BASE}/withdrawals/available`, { headers: authHeaders() })
  );
  return res.data;
}

export default {
  getBonusStatus,
  claimBonus,
  loginWithWebApp,
  ensureAuth,
  initiateDeposit,
  confirmDeposit,
  initiateWithdrawal,
  getBestPlayerProgress,
  initiatePromoWithdrawal,
  getWithdrawalAvailable,
  getToken,
};
